import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loginAPI, logoutAPI, LoginResponse, UserModulePermission } from '../services/api';

// User interface with name, role, and permissions fields
export interface User {
  name: string;
  role: string;
  userId?: number;
  userType?: string;
  token?: string;
  userPermissionList?: UserModulePermission[];
}

// AuthState interface with isAuthenticated and user fields
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
}

// AuthContextValue interface extending AuthState with login and logout methods
export interface AuthContextValue extends AuthState {
  login(username: string, password: string): Promise<void>;
  logout(): void;
}

// Storage key for sessionStorage
const AUTH_STORAGE_KEY = 'auth';

// Create the context with undefined default (will throw if used outside provider)
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Helper functions for sessionStorage with try/catch fallback
const loadAuthState = (): AuthState => {
  try {
    const stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load auth state from sessionStorage:', error);
  }
  return { isAuthenticated: false, user: null };
};

const saveAuthState = (state: AuthState): void => {
  try {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save auth state to sessionStorage:', error);
  }
};

// AuthProvider component with state management
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(loadAuthState);

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    saveAuthState(authState);
  }, [authState]);

  // Login method that calls the API
  const login = async (username: string, password: string): Promise<void> => {
    try {
      const response = await loginAPI({
        userName: username,
        password: password,
        userType: 'Admin',
        remember: false,
      });

      if (response.isSuccess && response.data) {
        const loginData = response.data as LoginResponse;
        setAuthState({
          isAuthenticated: true,
          user: {
            name: loginData.personName,
            role: loginData.userType,
            userId: loginData.userId,
            userType: loginData.userType,
            token: loginData.token,
            userPermissionList: loginData.userPermissionList,
          },
        });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    }
  };

  // Logout method that calls the API and clears authenticated state
  const logout = async (): Promise<void> => {
    try {
      await logoutAPI();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      setAuthState({
        isAuthenticated: false,
        user: null,
      });
    }
  };

  const value: AuthContextValue = {
    ...authState,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// useAuth custom hook that throws error if used outside provider
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
